import { Injectable, inject } from '@angular/core';
import { HttpRequest, HttpResponse } from '@angular/common/http';

import { MessageService } from '../message.service';
import { AppUtilsModule } from 'src/app/app-utils.module';

export interface RequestCacheEntry {
    url: string;
    response: HttpResponse<any>;
    lastRead: number;
}

// #docregion request-cache
export abstract class RequestCache {
    abstract get(req: HttpRequest<any>): HttpResponse<any> | undefined;
    abstract put(req: HttpRequest<any>, response: HttpResponse<any>): void;
}
// #enddocregion request-cache

const maxAge = 1000 * 60 * 2; // maximum cache age (ms)

@Injectable()
export class RequestCacheWithMap implements RequestCache {
    private messenger = inject(MessageService);

    cache = new Map<string, RequestCacheEntry>();
    private expiryQueue: Array<{ url: string; lastRead: number }> = [];

    get(req: HttpRequest<any>): HttpResponse<any> | undefined {
        const url = req.urlWithParams;
        const cached = this.cache.get(url);

        if (!cached) {
            return undefined;
        }

        const isExpired = cached.lastRead < Date.now() - maxAge;
        const expired = isExpired ? 'expired ' : '';
        this.messenger.add(`Found ${expired}cached response for "${url}".`);

        // if not expired get copy of the cached data
        return isExpired ? undefined : cached.response.clone({ body: AppUtilsModule.deepCopy(cached.response.body) });
    }

    put(req: HttpRequest<any>, response: HttpResponse<any>): void {
        const url = req.urlWithParams;
        this.messenger.add(`Caching response from "${url}".`);

        //create copy of the data in cache
        response = response.clone({ body: AppUtilsModule.deepCopy(response.body) });

        const entry = { url, response, lastRead: Date.now() };
        this.cache.set(url, entry);
        this.expiryQueue.push({ url, lastRead: entry.lastRead });

        // prune expired cache entries using a queue so we stop once entries are fresh
        const cutoff = Date.now() - maxAge;
        while (this.expiryQueue.length && this.expiryQueue[0].lastRead < cutoff) {
            const { url: expiredUrl, lastRead } = this.expiryQueue.shift()!;
            const current = this.cache.get(expiredUrl);
            if (current && current.lastRead === lastRead && current.lastRead < cutoff) {
                this.cache.delete(expiredUrl);
            }
        }

        this.messenger.add(`Request cache size: ${this.cache.size}.`);
    }
}
